'use client';

import { useEffect, useRef, useState } from 'react';
import { TbWorld } from 'react-icons/tb';
import { CircleFlagLanguage } from 'react-circle-flags';
import { getCurrentLanguage, changeLanguage, useNamespacedTranslate } from '@/shared/lib/locales';
import { LangCode } from '@/shared/lib/types';

const flagHeight = 18;
const languageItems: Array<{ code: LangCode; label: string; flagCode: string }> = [
  { code: LangCode.EN_GB, label: 'English', flagCode: 'en' },
  { code: LangCode.ES_ES, label: 'Español', flagCode: 'es' },
  { code: LangCode.PT_PT, label: 'Português', flagCode: 'pt' },
];

function normalizeLanguage(language: string | undefined): LangCode {
  if (!language) return LangCode.PT_PT;
  if (language.startsWith('en')) return LangCode.EN_GB;
  if (language.startsWith('es')) return LangCode.ES_ES;
  return LangCode.PT_PT;
}

export function LanguageDropdown() {
  const t = useNamespacedTranslate('languageDropdown');

  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<LangCode>(normalizeLanguage(getCurrentLanguage()));
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = async (language: LangCode) => {
    await changeLanguage(language);
    setCurrentLanguage(language);
    setLangDropdownOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (langDropdownRef.current && !langDropdownRef.current.contains(target)) {
        setLangDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={langDropdownRef}>
      <button
        onClick={() => setLangDropdownOpen((prev) => !prev)}
        className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none"
        aria-label={t('buttonAriaLabel')}
      >
        <TbWorld className="text-xl" />
      </button>

      {langDropdownOpen && (
        <div className="absolute right-0 top-12 z-50 min-w-[200px] overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-lg">
          {languageItems.map((item) => {
            const isActive = currentLanguage === item.code;
            return (
              <button
                key={item.code}
                onClick={() => handleLanguageChange(item.code)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition ${
                  isActive ? 'bg-slate-100 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white">
                  <CircleFlagLanguage languageCode={item.flagCode} height={flagHeight} />
                </span>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
